import { BuydownScenario } from './buydownCalculations';

export interface BreakevenDataPoint {
  month: number;
  cumulativeSavings: number;
  extraCashInvested: number;
  netPosition: number;
}

export interface BreakevenChartData {
  scenarioName: string;
  scenarioType: string;
  color: string;
  data: BreakevenDataPoint[];
  breakevenMonth: number | null;
  totalSavingsAt24Months: number;
  totalSavingsAt60Months: number;
}

export const calculateBreakevenData = (
  scenarios: BuydownScenario[],
  baselineScenario: BuydownScenario,
  timelineMonths: number = 60
): BreakevenChartData[] => {
  const colors = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))'];
  
  return scenarios
    .filter(s => s.type !== 'fixed')
    .map((scenario, idx) => {
      const data: BreakevenDataPoint[] = [];
      let cumulativeSavings = 0;
      let breakevenMonth = null;
      
      // Helper function to get payment for a specific month
      const getPaymentForMonth = (month: number, paymentSchedule: BuydownScenario['paymentSchedule']): number => {
        if (paymentSchedule.length === 1) {
          return paymentSchedule[0].monthlyPayment;
        }
        
        // Determine which period based on the period string
        if (scenario.type === '1/0') {
          // Year 1 is months 1-12, Year 2+ is 13+
          return month <= 12 ? paymentSchedule[0].monthlyPayment : paymentSchedule[1].monthlyPayment;
        } else if (scenario.type === '2/1') {
          // Year 1 is months 1-12, Year 2 is 13-24, Year 3+ is 25+
          if (month <= 12) return paymentSchedule[0].monthlyPayment;
          if (month <= 24) return paymentSchedule[1].monthlyPayment;
          return paymentSchedule[2].monthlyPayment;
        } else if (scenario.type === '3/2/1') {
          // Year 1 is months 1-12, Year 2 is 13-24, Year 3 is 25-36, Year 4+ is 37+
          if (month <= 12) return paymentSchedule[0].monthlyPayment;
          if (month <= 24) return paymentSchedule[1].monthlyPayment;
          if (month <= 36) return paymentSchedule[2].monthlyPayment;
          return paymentSchedule[3].monthlyPayment;
        }
        
        return paymentSchedule[paymentSchedule.length - 1].monthlyPayment;
      };
      
      for (let month = 0; month <= timelineMonths; month++) {
        if (month === 0) {
          data.push({
            month: 0,
            cumulativeSavings: 0,
            extraCashInvested: scenario.extraCashNeeded,
            netPosition: -scenario.extraCashNeeded
          });
        } else {
          const currentScenarioPayment = getPaymentForMonth(month, scenario.paymentSchedule);
          const baselinePayment = baselineScenario.paymentSchedule[0].monthlyPayment;
          const monthlySaving = baselinePayment - currentScenarioPayment;
          
          cumulativeSavings += monthlySaving;
          const netPosition = cumulativeSavings - scenario.extraCashNeeded;
          
          data.push({
            month,
            cumulativeSavings,
            extraCashInvested: scenario.extraCashNeeded,
            netPosition
          });
          
          if (breakevenMonth === null && netPosition >= 0) {
            breakevenMonth = month;
          }
        }
      }
      
      return {
        scenarioName: scenario.name,
        scenarioType: scenario.type,
        color: colors[idx] || colors[0],
        data,
        breakevenMonth,
        totalSavingsAt24Months: data[24]?.netPosition || 0,
        totalSavingsAt60Months: data[60]?.netPosition || 0
      };
    });
};
